import type { RiskFlag, RiskType } from "@screener/contracts";

/**
 * 하드필터 = 수익성 계산 전에 물건을 후보에서 빼는 규칙.
 * 여기 걸리면 얼마나 싸든 검토하지 않는다.
 *
 * 압류(seizure)는 사용자 투자 원칙이 명시적으로 예외를 정한 유일한 항목이다.
 *   "압류 걸린건 건들지 않는다. 다만 공무원을 통해서 압류를 확인할 수 있다면
 *    이건 압류를 풀 수 있는지로 본다."
 * 즉 기본은 제외이고, 1차 출처(공무원) 확인이 있을 때만
 * '해제 가능 여부를 따지는 단계'로 넘긴다. 자동 통과가 아니다.
 */

export type Verdict = "exclude" | "review" | "pass";

export interface RuleOutcome {
  riskType: RiskType;
  verdict: Verdict;
  reason: string;
}

export interface HardFilterResult {
  /** 세 값 중 가장 보수적인 결론. exclude > review > pass */
  verdict: Verdict;
  outcomes: RuleOutcome[];
  excluded: RiskType[];
  needsReview: RiskType[];
}

const SEVERITY: Record<Verdict, number> = { pass: 0, review: 1, exclude: 2 };

/** unknown(모름)을 absent(없음)로 취급하지 않는 항목들 */
const UNKNOWN_IS_REVIEW: ReadonlySet<RiskType> = new Set<RiskType>([
  "seizure",
  "lien",
  "share_sale",
  "senior_provisional_registration",
  "senior_injunction",
  "tax_arrears",
  "tenant_opposing_power",
]);

function evaluateOne(flag: RiskFlag): RuleOutcome {
  const { riskType, state, verifiedByOfficial } = flag;

  if (state === "absent") {
    return { riskType, verdict: "pass", reason: "해당 위험 없음으로 확인" };
  }

  if (state === "unknown") {
    return UNKNOWN_IS_REVIEW.has(riskType)
      ? {
          riskType,
          verdict: "review",
          reason: "확인되지 않음 — 모름을 없음으로 취급하지 않는다",
        }
      : { riskType, verdict: "pass", reason: "확인되지 않았으나 하드필터 대상 아님" };
  }

  // state === "present"
  if (riskType === "seizure") {
    return verifiedByOfficial
      ? {
          riskType,
          verdict: "review",
          reason:
            "압류 있음. 공무원 확인이 되었으므로 해제 가능 여부만 따진다 (투자 원칙 예외)",
        }
      : {
          riskType,
          verdict: "exclude",
          reason:
            "압류 있음. 공무원 확인 경로가 없으면 건들지 않는다 (투자 원칙 기본)",
        };
  }

  return { riskType, verdict: "exclude", reason: "하드필터 해당 — 검토 대상에서 제외" };
}

export function evaluateHardFilter(flags: readonly RiskFlag[]): HardFilterResult {
  const outcomes = flags.map(evaluateOne);
  let verdict: Verdict = "pass";
  for (const o of outcomes) {
    if (SEVERITY[o.verdict] > SEVERITY[verdict]) verdict = o.verdict;
  }
  return {
    verdict,
    outcomes,
    excluded: outcomes.filter((o) => o.verdict === "exclude").map((o) => o.riskType),
    needsReview: outcomes.filter((o) => o.verdict === "review").map((o) => o.riskType),
  };
}

export const RISK_LABEL_KO: Record<RiskType, string> = {
  seizure: "압류",
  lien: "유치권",
  share_sale: "지분 매각",
  statutory_superficies: "법정지상권",
  senior_provisional_registration: "선순위 가등기",
  senior_injunction: "선순위 가처분",
  resale: "재매각",
  tenant_opposing_power: "대항력 있는 임차인",
  tax_arrears: "조세 체납",
};
